import { getContrastRatio, type PaletteColor } from "@mui/material/styles";
import { ASSISTANT_AVATAR } from "../../../theme/tokens";
import { AssistantAvatarColor, AssistantAvatarHairColor, AssistantAvatarSkinTone } from "./assistantAvatarConfig";

/** The resolved stops every part paints with — a fixed token or the org's primary. */
export interface AvatarBodyPalette {
	light: string;
	main: string;
	featureInk: string;
}

type FixedBodyColor = Exclude<AssistantAvatarColor, AssistantAvatarColor.Theme>;

const toPalette = (stops: { LIGHT: string; MAIN: string; FEATURE_INK: string }): AvatarBodyPalette => ({
	light: stops.LIGHT,
	main: stops.MAIN,
	featureInk: stops.FEATURE_INK,
});

const FIXED_BODY: Record<FixedBodyColor, AvatarBodyPalette> = {
	[AssistantAvatarColor.Lavender]: toPalette(ASSISTANT_AVATAR.BODY.LAVENDER),
	[AssistantAvatarColor.Gold]: toPalette(ASSISTANT_AVATAR.BODY.GOLD),
	[AssistantAvatarColor.Mint]: toPalette(ASSISTANT_AVATAR.BODY.MINT),
	[AssistantAvatarColor.Coral]: toPalette(ASSISTANT_AVATAR.BODY.CORAL),
	[AssistantAvatarColor.Sky]: toPalette(ASSISTANT_AVATAR.BODY.SKY),
	[AssistantAvatarColor.Midnight]: toPalette(ASSISTANT_AVATAR.BODY.MIDNIGHT),
};

/** Worst-case contrast of an ink across both gradient stops, since the face straddles the middle. */
const minContrast = (ink: string, light: string, main: string): number => Math.min(getContrastRatio(ink, light), getContrastRatio(ink, main));

// An org can pick any primary, dark or pale, so the theme body can't hardcode its ink like the fixed ones do.
const pickFeatureInk = (light: string, main: string): string => {
	const { ON_LIGHT, ON_DARK } = ASSISTANT_AVATAR.FEATURE_INK;
	return minContrast(ON_LIGHT, light, main) >= minContrast(ON_DARK, light, main) ? ON_LIGHT : ON_DARK;
};

export const resolveBodyPalette = (color: AssistantAvatarColor, primary: PaletteColor): AvatarBodyPalette => {
	if (color !== AssistantAvatarColor.Theme) return FIXED_BODY[color];
	return { light: primary.light, main: primary.main, featureInk: pickFeatureInk(primary.light, primary.main) };
};

export const SKIN_BY_TONE: Record<AssistantAvatarSkinTone, string> = {
	[AssistantAvatarSkinTone.Tone1]: ASSISTANT_AVATAR.SKIN.TONE_1,
	[AssistantAvatarSkinTone.Tone2]: ASSISTANT_AVATAR.SKIN.TONE_2,
	[AssistantAvatarSkinTone.Tone3]: ASSISTANT_AVATAR.SKIN.TONE_3,
	[AssistantAvatarSkinTone.Tone4]: ASSISTANT_AVATAR.SKIN.TONE_4,
	[AssistantAvatarSkinTone.Tone5]: ASSISTANT_AVATAR.SKIN.TONE_5,
	[AssistantAvatarSkinTone.Tone6]: ASSISTANT_AVATAR.SKIN.TONE_6,
};

export const HAIR_BY_COLOR: Record<AssistantAvatarHairColor, string> = {
	[AssistantAvatarHairColor.Black]: ASSISTANT_AVATAR.HAIR.BLACK,
	[AssistantAvatarHairColor.Brown]: ASSISTANT_AVATAR.HAIR.BROWN,
	[AssistantAvatarHairColor.Chestnut]: ASSISTANT_AVATAR.HAIR.CHESTNUT,
	[AssistantAvatarHairColor.Auburn]: ASSISTANT_AVATAR.HAIR.AUBURN,
	[AssistantAvatarHairColor.Blonde]: ASSISTANT_AVATAR.HAIR.BLONDE,
	[AssistantAvatarHairColor.Silver]: ASSISTANT_AVATAR.HAIR.SILVER,
	[AssistantAvatarHairColor.Pink]: ASSISTANT_AVATAR.HAIR.PINK,
	[AssistantAvatarHairColor.Blue]: ASSISTANT_AVATAR.HAIR.BLUE,
};
